import { dontUpsell } from '@trello/config';
import {
  useUpgradePromptDismissMessageMutation,
  useUpgradePromptQuery,
} from '@trello/graphql/generated';
import { useState } from 'react';

interface UseUpgradePrompt {
  (args: { orgId: string; messageId: string; allowUpsell?: boolean }): {
    dismissMessage: () => void;
    hidePrompt: () => void;
    shouldDisplayUpgradePrompt: boolean;
  };
}

// UpgradePrompt display state logic
export const useUpgradePrompt: UseUpgradePrompt = ({
  orgId,
  messageId,
  allowUpsell,
}) => {
  // To avoid making excessive requests we need to pre-cache the members organizations and oneTimeDissmissedMessages.
  // There was an issue in https://bitbucket.org/trello/web/pull-requests/5105/wlrs-827-dont-call-members-route-when-on/diff
  // where we tried to remove a member fetch but since the apollo cache wasn't populated at this point
  // apollo ended up making excessive requests which gets rated limited if you have alot of teams
  // to solve this we had to fetch this minimum amount of member data with loadMinimumMemberDataForTemplatePage
  const { data } = useUpgradePromptQuery({
    variables: {
      memberId: 'me',
      orgId,
    },
  });
  const [
    addOneTimeMessagesDismissed,
  ] = useUpgradePromptDismissMessageMutation();

  const isFreeTeam = Boolean(
    data?.organization && !data.organization.products.length,
  );

  const isMessageDismissed = Boolean(
    data?.member?.oneTimeMessagesDismissed?.find(
      (msgId) => msgId === messageId,
    ),
  );

  // Manual override to speed up hiding the prompt
  const [shouldHidePrompt, togglePrompt] = useState(false);

  const shouldDisplayUpgradePrompt = Boolean(
    (!dontUpsell || allowUpsell) &&
      isFreeTeam &&
      !shouldHidePrompt &&
      !isMessageDismissed,
  );

  return {
    dismissMessage() {
      addOneTimeMessagesDismissed({
        variables: {
          memberId: 'me',
          messageId,
        },
      });
    },
    hidePrompt() {
      togglePrompt(true);
    },
    shouldDisplayUpgradePrompt,
  };
};
